import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { X, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CompanySettingsDialog({ open, onOpenChange, companySettings, setCompanySettings, user, onSaved }) {
  const [biWeeklyStartDate, setBiWeeklyStartDate] = useState(companySettings.bi_weekly_start_date || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [progressCategories, setProgressCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Sync bi-weekly date when companySettings updates
  useEffect(() => {
    if (companySettings.bi_weekly_start_date) {
      setBiWeeklyStartDate(companySettings.bi_weekly_start_date);
    }
  }, [companySettings.bi_weekly_start_date]);

  useEffect(() => {
    if (open) fetchCategories();
  }, [open]);

  const fetchCategories = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/company/progress-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProgressCategories(data.categories || []);
      }
    } catch (e) {}
  };

  const saveCategories = async (cats) => {
    setLoadingCategories(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/company/progress-categories`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories: cats })
      });
      if (res.ok) {
        setProgressCategories(cats);
        toast.success('Progress categories updated');
      } else {
        toast.error('Failed to update categories');
      }
    } catch {
      toast.error('Connection error');
    }
    setLoadingCategories(false);
  };

  const addCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    if (progressCategories.includes(trimmed)) {
      toast.error('Category already exists');
      return;
    }
    saveCategories([...progressCategories, trimmed]);
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    saveCategories(progressCategories.filter(c => c !== cat));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file must be less than 2MB');
      return;
    }
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setCompanySettings({ ...companySettings, logo_url: data.url });
        setLogoPreviewUrl(data.url);
        toast.success('Logo uploaded');
      } else {
        toast.error('Failed to upload logo');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: companySettings.name,
          tagline: companySettings.tagline,
          logo_url: companySettings.logo_url,
          name_animation: companySettings.name_animation,
          logo_animation: companySettings.logo_animation,
          name_color: companySettings.name_color,
          name_font: companySettings.name_font,
          timezone: companySettings.timezone,
          bi_weekly_start_date: biWeeklyStartDate,
          default_shift_start: companySettings.default_shift_start,
          default_shift_end: companySettings.default_shift_end,
          auto_clockout_hours: companySettings.auto_clockout_hours ? parseFloat(companySettings.auto_clockout_hours) : undefined
        })
      });
      if (response.ok) {
        toast.success('Company settings saved successfully');
        onOpenChange(false);
        if (onSaved) onSaved();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Company Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Company Name</Label>
            <Input
              type="text"
              value={companySettings.name || ''}
              onChange={(e) => setCompanySettings({ ...companySettings, name: e.target.value })}
              placeholder="Enter company name"
              disabled={user.role !== 'company_admin'}
            />
            <p className="text-sm text-gray-500 mt-1">Displayed in one line in the dashboard header</p>
            {user.role !== 'company_admin' && (
              <p className="text-sm text-amber-600 mt-1">Only Company Admin can change company name</p>
            )}
          </div>
          
          <div>
            <Label>Company Tagline</Label>
            <Input
              type="text"
              value={companySettings.tagline || ''}
              onChange={(e) => setCompanySettings({ ...companySettings, tagline: e.target.value })}
              placeholder="Enter company tagline"
              disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
            />
            <p className="text-sm text-gray-500 mt-1">Shown below company name in smaller font</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name Font Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={companySettings.name_color || '#1e293b'}
                  onChange={(e) => setCompanySettings({ ...companySettings, name_color: e.target.value })}
                  disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
                  className="w-12 h-10 border rounded cursor-pointer disabled:opacity-50"
                />
                <Input
                  value={companySettings.name_color || '#1e293b'}
                  onChange={(e) => setCompanySettings({ ...companySettings, name_color: e.target.value })}
                  disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
                  className="flex-1"
                  placeholder="#1e293b"
                />
              </div>
            </div>
            <div>
              <Label>Name Font</Label>
              <Select
                value={companySettings.name_font || 'Inter'}
                onValueChange={(val) => setCompanySettings({ ...companySettings, name_font: val })}
                disabled={!['company_admin', 'general_manager', 'manager'].includes(user.role)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inter">Inter</SelectItem>
                  <SelectItem value="Poppins">Poppins</SelectItem>
                  <SelectItem value="Roboto">Roboto</SelectItem>
                  <SelectItem value="Montserrat">Montserrat</SelectItem>
                  <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                  <SelectItem value="Oswald">Oswald</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Company Logo</Label>
            <div className="flex items-center gap-4 mt-1">
              {(logoPreviewUrl || companySettings.logo_url) && (
                <img
                  src={logoPreviewUrl || companySettings.logo_url}
                  alt="Logo preview"
                  className="w-16 h-16 object-contain border rounded"
                />
              )}
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo || !['company_admin', 'general_manager', 'manager'].includes(user.role)}
                />
                <p className="text-xs text-gray-500 mt-1">Max 2MB. PNG or JPG recommended.</p>
              </div>
            </div>
          </div>

          <div>
            <Label>Timezone</Label>
            <Select
              value={companySettings.timezone || 'America/New_York'}
              onValueChange={(val) => setCompanySettings({ ...companySettings, timezone: val })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="America/Toronto">Toronto (ET)</SelectItem>
                <SelectItem value="America/Vancouver">Vancouver (PT)</SelectItem>
                <SelectItem value="Europe/London">London (GMT)</SelectItem>
                <SelectItem value="Australia/Sydney">Sydney (AEST)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Bi-Weekly Pay Period Start Date</Label>
            <Input
              type="date"
              value={biWeeklyStartDate}
              onChange={(e) => setBiWeeklyStartDate(e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              This date defines the start of your pay cycles. All bi-weekly calculations will align to this date.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name Animation</Label>
              <Select
                value={companySettings.name_animation || 'none'}
                onValueChange={(val) => setCompanySettings({ ...companySettings, name_animation: val })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="fade">Fade In</SelectItem>
                  <SelectItem value="slide">Slide In</SelectItem>
                  <SelectItem value="bounce">Bounce</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Logo Animation</Label>
              <Select
                value={companySettings.logo_animation || 'none'}
                onValueChange={(val) => setCompanySettings({ ...companySettings, logo_animation: val })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="spin">Spin</SelectItem>
                  <SelectItem value="pulse">Pulse</SelectItem>
                  <SelectItem value="bounce">Bounce</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Universal Shift & Auto Clock-Out Settings */}
          {user.role === 'company_admin' && (
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold mb-2 block">Universal Shift & Auto Clock-Out</Label>
              <p className="text-xs text-gray-500 mb-3">Default shift times and auto clock-out for all projects. Individual projects can override these.</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Default Shift Start</Label>
                  <Input
                    type="time"
                    value={companySettings.default_shift_start || '07:00'}
                    onChange={(e) => setCompanySettings({ ...companySettings, default_shift_start: e.target.value })}
                    data-testid="default-shift-start"
                  />
                </div>
                <div>
                  <Label className="text-xs">Default Shift End</Label>
                  <Input
                    type="time"
                    value={companySettings.default_shift_end || '15:00'}
                    onChange={(e) => setCompanySettings({ ...companySettings, default_shift_end: e.target.value })}
                    data-testid="default-shift-end"
                  />
                </div>
                <div>
                  <Label className="text-xs">Auto Clock-Out (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    step="0.5"
                    value={companySettings.auto_clockout_hours || 12}
                    onChange={(e) => setCompanySettings({ ...companySettings, auto_clockout_hours: e.target.value })}
                    data-testid="auto-clockout-hours"
                  />
                  <p className="text-xs text-gray-400 mt-1">Employees auto-clocked out after this many hours</p>
                </div>
              </div>
            </div>
          )}

          {/* Progress Categories Management */}
          {user.role === 'company_admin' && (
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold mb-2 block">Project Progress Categories</Label>
              <p className="text-xs text-gray-500 mb-3">These statuses appear in the field employee "Report Project Progress" form.</p>
              <div className="space-y-2 mb-3">
                {progressCategories.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 border">
                    <span className="text-sm">{cat}</span>
                    <button
                      onClick={() => removeCategory(cat)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      disabled={loadingCategories}
                      data-testid={`remove-category-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="New category name..."
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                  data-testid="new-category-input"
                />
                <Button variant="outline" size="sm" onClick={addCategory} disabled={loadingCategories || !newCategory.trim()} data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Settings</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CompanySettingsDialog;
